/**
 * Public /pricing page — replaces the old "Sectors" duplicate. Shows the three
 * self-serve packages up top, then a custom-package enquiry section below.
 *
 * Wires each package card into the shared PurchaseModal via a shared header
 * bus — see PurchaseModal for the preselect logic on `startPackageId`.
 */
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PurchaseModal } from "@/components/PurchaseModal";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT, getLocale } from "@/lib/i18n";
import { Loader2, CheckCircle2, Sparkles, Star, FileText, ArrowRight, ClipboardList, Wrench, FileSignature, Rocket } from "lucide-react";
import { money } from "@/lib/numfmt";

const PROJECT_TYPES = ["Industrial", "Mining", "Utilities", "Telecom", "Other"];
const PROJECT_TYPES_FR = { Industrial: "Industrie", Mining: "Mines", Utilities: "Secteur électrique", Telecom: "Télécoms", Other: "Autre" };

// A single pricing card. Extracted so row-1 and row-2 look identical without
// duplicating markup. Row-2 (portfolio) cards use the same visual style but
// carry no "Recommended"/"Best value" badge — that's a data-driven decision
// controlled by the caller populating `p.badge`.
const renderPricingCard = (p, onBuy) => {
  const Icon = p.icon;
  return (
    <div key={p.id} className="card p-6 relative flex flex-col" data-testid={`pkg-card-${p.id}`}
          style={{ border: p.badge?.tone === "brand" ? "2px solid var(--brand-green)" : "1px solid var(--rule)" }}>
      {p.badge && (
        <span className="absolute -top-2 left-4 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                style={{ background: p.badge.tone === "accent" ? "#E58B25" : "var(--brand-green)", color: "#fff" }}
                data-testid={`pkg-badge-${p.id}`}>{p.badge.label}</span>
      )}
      <div className="flex items-baseline gap-2 mt-1">
        <Icon size={16} style={{ color: p.badge?.tone === "accent" ? "#E58B25" : "var(--brand-green)" }} />
        <div className="font-display text-lg" style={{ color: "var(--char)" }}>{p.title}</div>
      </div>
      <div className="font-display text-4xl mt-3" style={{ color: "var(--char)" }}>{money(p.price)}</div>
      <div className="text-sm mt-2" style={{ color: "var(--brand-green)", fontWeight: 600 }}>{p.credits_label}</div>
      <p className="text-sm mt-3 flex-1" style={{ color: "var(--muted)" }}>{p.summary}</p>
      <button type="button" onClick={() => onBuy(p.id)} className="btn-primary mt-4 !justify-center" data-testid={`pkg-buy-${p.id}`}>
        {p.cta} <ArrowRight size={14} />
      </button>
    </div>
  );
};

const Pricing = () => {
  const { user, checkAuth } = useAuth();
  const t = useT();
  const lang = getLocale();
  const isFr = lang === "fr";
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [startPkg, setStartPkg] = useState(null);
  const [lockedProjects, setLockedProjects] = useState([]);

  // Enquiry form state
  const [form, setForm] = useState({ full_name: "", business_email: "", company: "", country_region: "", project_type: "Industrial", project_note: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/projects").then((r) => {
      setLockedProjects((r.data || []).filter((p) => !p.paid));
    }).catch(() => {});
  }, [user]);

  const packages = useMemo(() => [
    { id: "single_report", title: isFr ? "Rapport détaillé" : "Detailed report", price: 99,
      credits_label: isFr ? "Prix de lancement" : "Launch price",
      cta: isFr ? "Acheter le rapport" : "Buy the report",
      summary: isFr
        ? "Le périmètre numérique complet\u00a0: modèle de puissance, équipements, coûts budgétaires, permis, note de faisabilité et un plan de projet et financier initial."
        : "The full digital scope: power model, equipment, budgetary costs, permitting, feasibility memo and an initial project & financial plan.",
      badge: { label: isFr ? "Le plus choisi" : "Most popular", tone: "brand" }, icon: FileText, row: 1 },
    { id: "credit_bundle_2", title: isFr ? "2\u00a0rapports" : "2 reports", price: 180,
      credits_label: isFr ? "~90\u00a0$ chacun · économisez ~9\u00a0%" : "~$90 each · save ~9%",
      cta: isFr ? "Obtenir 2\u00a0crédits" : "Get 2 report credits",
      summary: isFr
        ? "Rapport + modèle Excel à neuf onglets + CSV de comparaison."
        : "Report + nine-tab Excel model + comparison CSV.",
      icon: Sparkles, row: 1 },
    { id: "credit_bundle_3", title: isFr ? "3\u00a0rapports" : "3 reports", price: 249,
      credits_label: isFr ? "~83\u00a0$ chacun · économisez ~16\u00a0%" : "~$83 each · save ~16%",
      cta: isFr ? "Obtenir 3\u00a0crédits" : "Get 3 report credits",
      summary: isFr
        ? "Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel ingénieur de 30 minutes offert.*"
        : "Report + nine-tab Excel model + comparison CSV + a complimentary 30-minute engineer call.*",
      icon: Sparkles, row: 1 },
    { id: "credit_bundle_5", title: isFr ? "5\u00a0rapports" : "5 reports", price: 375,
      credits_label: isFr ? "~75\u00a0$ chacun · économisez ~24\u00a0%" : "~$75 each · save ~24%",
      cta: isFr ? "Obtenir 5\u00a0crédits" : "Get 5 report credits",
      summary: isFr
        ? "Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel ingénieur d'une heure offert.*"
        : "Report + nine-tab Excel model + comparison CSV + a complimentary 1-hour engineer call.*",
      badge: { label: isFr ? "Meilleure valeur · Économisez 120\u00a0$" : "Best Value · Save $120", tone: "accent" }, icon: Star, row: 2 },
    { id: "credit_bundle_10", title: isFr ? "10\u00a0rapports" : "10 reports", price: 650,
      credits_label: isFr ? "~65\u00a0$ chacun · économisez ~34\u00a0%" : "~$65 each · save ~34%",
      cta: isFr ? "Obtenir 10\u00a0crédits" : "Get 10 report credits",
      summary: isFr
        ? "Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel ingénieur de 2 heures offert + une esquisse de mandat de développement.*"
        : "Report + nine-tab Excel model + comparison CSV + a complimentary 2-hour engineer call + a development-mandate outline.*",
      icon: Sparkles, row: 2 },
    { id: "credit_bundle_20", title: isFr ? "20\u00a0rapports" : "20 reports", price: 1100,
      credits_label: isFr ? "~55\u00a0$ chacun · économisez ~44\u00a0%" : "~$55 each · save ~44%",
      cta: isFr ? "Obtenir 20\u00a0crédits" : "Get 20 report credits",
      summary: isFr
        ? "Rapport + modèle Excel à neuf onglets + CSV de comparaison + un appel ingénieur de 4 heures offert + une esquisse de mandat de développement + un modèle d'étude de pré-faisabilité.*"
        : "Report + nine-tab Excel model + comparison CSV + a complimentary 4-hour engineer call + a development-mandate outline + a pre-feasibility study pattern.*",
      icon: Sparkles, row: 2 },
  ], [isFr]);

  const isLive = (p) => typeof p.price === "number" && p.price > 0;
  const row1 = packages.filter((p) => p.row === 1 && isLive(p));
  const row2 = packages.filter((p) => p.row === 2 && isLive(p));

  const onBuy = (pkgId) => {
    // Every package is now a credit purchase — no project selection required
    // for any of them. Open the modal with the tapped package preselected.
    setStartPkg(pkgId);
    setPurchaseOpen(true);
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (busy || sent) return;
    if (!form.full_name.trim() || !form.business_email.trim() || !form.company.trim() || !form.country_region.trim()) {
      toast.error(isFr ? "Merci de remplir tous les champs requis" : "Please complete the required fields"); return;
    }
    setBusy(true);
    try {
      await api.post("/custom-enquiry", { ...form, language: lang });
      setSent(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || (isFr ? "Envoi impossible pour le moment" : "Could not submit — please try again");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-6">
          <div className="overline" data-testid="pricing-overline">{isFr ? "Tarifs" : "Pricing"}</div>
          <h1 className="font-display text-4xl mt-1" style={{ color: "var(--char)" }} data-testid="pricing-title">
            {isFr ? "Débloquez le rapport de faisabilité de votre projet" : "Unlock your project's feasibility report"}
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--muted)", maxWidth: 640 }}>
            {isFr ? "Rapports auto-générés à la demande, ou un accompagnement de développement de projet sur mesure." : "Self-serve reports on demand, or a bespoke project-development engagement."}
          </p>
        </div>

        {/* Row 1 — the three self-serve packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-grid">
          {row1.map((p) => renderPricingCard(p, onBuy))}
        </div>

        {/* Row 2 — larger portfolio bundles. Only rendered when prices are enabled. */}
        {row2.length > 0 && (
          <div className="mt-10" data-testid="pricing-row-2">
            <h2 className="font-display text-2xl mb-4" style={{ color: "var(--char)" }} data-testid="pricing-row-2-title">
              {isFr ? "Packs pour portefeuilles plus larges" : "Larger portfolio bundles"}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {row2.map((p) => renderPricingCard(p, onBuy))}
            </div>
          </div>
        )}

        {/* Footnote for the "*" carried by the 3/5/10/20 card copy. Required by
            that copy — without it the asterisks dangle. Same muted body style
            as the page intro; no new layout. */}
        <p className="text-xs mt-6" style={{ color: "var(--muted)", maxWidth: 760 }} data-testid="pricing-footnote">
          {isFr
            ? "* Les appels ingénieur, l'esquisse de mandat de développement et le modèle d'étude de pré-faisabilité sont offerts et organisés par e-mail après l'achat. Ils ne sont pas vendus via Paddle. Crédits de rapport prépayés, utilisables sous 12 mois."
            : "* Engineer calls, the development-mandate outline and the pre-feasibility study pattern are complimentary and arranged by email after purchase. They are not sold through Paddle. Prepaid report credits are redeemable within 12 months."}
        </p>

        {/* Custom package section */}
        <section className="mt-14 rounded-lg overflow-hidden" data-testid="custom-package-section"
                  style={{ background: "linear-gradient(135deg, #16211D 0%, #1A6F2B 100%)", color: "#fff" }}>
          <div className="p-8 md:p-10">
            <div className="overline" style={{ color: "#B4C97F" }}>
              {isFr ? "Sur arrangement" : "By arrangement"}
            </div>
            <h2 className="font-display text-3xl mt-1" style={{ color: "#fff" }} data-testid="custom-title">
              {isFr ? "Package de développement de projet personnalisé, sur arrangement" : "Customised project development package, by arrangement"}
            </h2>
            <p className="text-sm mt-3" style={{ color: "#E4F3E7", maxWidth: 640 }}>
              {isFr ? "Pour des clients aux projets plus larges, complexes ou multiples. Nous concevons un accompagnement dédié de bout en bout." : "For customers with larger, more complex, or multi-project needs. We design a dedicated end-to-end engagement."}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8" data-testid="custom-workflow">
              {[
                { icon: ClipboardList, label: isFr ? "Partagez les détails initiaux du projet" : "Share initial project details" },
                { icon: Wrench,        label: isFr ? "Nous étudions type, priorités et exigences" : "Review project type, priorities, and requirements" },
                { icon: FileSignature, label: isFr ? "Proposition détaillée\u00a0: périmètre, calendrier et devis" : "Receive a tailored scope, timeline, and proposal" },
                { icon: Rocket,        label: isFr ? "Démarrage du package sur mesure convenu" : "Begin the agreed customised development package" },
              ].map((step, i) => {
                const Ic = step.icon;
                return (
                  <div key={i} className="p-4 rounded-md" style={{ background: "rgba(255,255,255,0.08)" }}>
                    <div className="flex items-center gap-2">
                      <Ic size={16} style={{ color: "#B4C97F" }} />
                      <div className="text-xs uppercase tracking-wider" style={{ color: "#B4C97F" }}>{isFr ? "Étape" : "Step"} {i + 1}</div>
                    </div>
                    <div className="text-sm mt-2">{step.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Enquiry form */}
            <div className="mt-10 p-6 rounded-md" style={{ background: "#fff", color: "var(--char)" }} data-testid="custom-form-wrap">
              {sent ? (
                <div className="text-center py-8" data-testid="custom-form-sent">
                  <CheckCircle2 size={28} style={{ color: "var(--brand-green)", margin: "0 auto" }} />
                  <div className="font-display text-xl mt-3">
                    {isFr ? "Merci — nous avons reçu votre demande" : "Thanks — we've received your enquiry"}
                  </div>
                  <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
                    {isFr ? "Un email de confirmation vous a été envoyé. Un membre de notre équipe reviendra vers vous." : "A confirmation email is on its way. A member of our team will get back to you."}
                  </p>
                </div>
              ) : (
                <form onSubmit={submitEnquiry} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label={isFr ? "Nom complet *" : "Full name *"} testid="cf-full-name"
                            value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required />
                    <Field label={isFr ? "E-mail professionnel *" : "Business email *"} testid="cf-email"
                            type="email" value={form.business_email} onChange={(v) => setForm({ ...form, business_email: v })} required />
                    <Field label={isFr ? "Entreprise ou organisation *" : "Company or organisation *"} testid="cf-company"
                            value={form.company} onChange={(v) => setForm({ ...form, company: v })} required />
                    <Field label={isFr ? "Pays ou région *" : "Country or region *"} testid="cf-country"
                            value={form.country_region} onChange={(v) => setForm({ ...form, country_region: v })} required />
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{ color: "var(--muted)" }}>
                        {isFr ? "Type de projet *" : "Project type *"}
                      </label>
                      <select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })}
                                className="w-full px-3 py-2 text-sm rounded" style={{ border: "1px solid var(--hairline)" }} data-testid="cf-project-type">
                        {PROJECT_TYPES.map((t) => <option key={t} value={t}>{isFr ? PROJECT_TYPES_FR[t] : t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{ color: "var(--muted)" }}>
                      {isFr ? "Note de projet (optionnel)" : "Short project note (optional)"}
                    </label>
                    <textarea rows={3} value={form.project_note} onChange={(e) => setForm({ ...form, project_note: e.target.value })}
                                maxLength={2000} className="w-full px-3 py-2 text-sm rounded" style={{ border: "1px solid var(--hairline)" }} data-testid="cf-note" />
                  </div>
                  {/* Honeypot */}
                  <label style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                    Website<input type="text" tabIndex={-1} autoComplete="off"
                                    value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                  </label>
                  <div className="flex justify-end">
                    <button type="submit" disabled={busy} className="btn-primary" data-testid="cf-submit">
                      {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                      {isFr ? "Demander un package sur mesure" : "Request a custom package"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <PurchaseModal open={purchaseOpen} onClose={() => setPurchaseOpen(false)}
                       lockedProjects={lockedProjects}
                       startProjectId={null}
                       startPackageId={startPkg}
                       onPurchaseComplete={() => { checkAuth(); }} />
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", required = false, testid }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{ color: "var(--muted)" }}>{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
             className="w-full px-3 py-2 text-sm rounded" style={{ border: "1px solid var(--hairline)" }} data-testid={testid} />
  </div>
);

export default Pricing;
